import React, { useContext, useEffect, useState, forwardRef } from 'react';
import { Avatar, Tag } from 'antd';
import AspectCard from '@/component/AspectCard';
import { AudioIcon } from '@/component/ToggleIcon';
import VideoView from './VideoView';
import { ParticipantType } from '@/types';
import Tool from '@/pages/Meeting/Tool';
import style from './style.module.scss';
import { useRTM } from '@/component/Rtm/index';
import { MeetingContext } from '@/pages/Meeting';

interface PropType {
  participant: ParticipantType;
  active?: boolean;
  className?: string;
  compact?: boolean;
  showTag?: boolean;
  showTool?: boolean;
  cover?: boolean;
  caption?: boolean;
}

const VideoCard = forwardRef<HTMLDivElement, PropType>((props, ref) => {
  const { participant, className, compact, showTag = true, showTool = true, cover, caption = true  } = props;
  const { isMonitorMode } = useContext(MeetingContext);
  const { isRTMConnected, connectRTM, disconnectRTM, addEventListener, removeEventListener, rtmPublish } = useRTM();
  const [subtitles, setSubtitles] = useState<string[]>([]);

  // Extract roomId from URL
  const getRoomIdFromURL = (): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('meeting') || null;
  };

  const roomId = getRoomIdFromURL();

  useEffect(() => {
    if (roomId) {
      // Connect to RTM when component mounts
      connectRTM();

      const handleSubtitleMessage = (message: string) => {
        try {
          const parsedMessage = JSON.parse(message);
          if (parsedMessage.action === 'subtitle' && parsedMessage.roomId === roomId) {
            setSubtitles((prevSubtitles) => [...prevSubtitles, parsedMessage.data]);
          }
        } catch (error) {
          console.error('Error parsing subtitle message:', error);
        }
      };

      if (isRTMConnected) {
        addEventListener('message', handleSubtitleMessage);

        return () => {
          removeEventListener('message', handleSubtitleMessage);
        };
      }
       
      // Clean up connection when component unmounts
    // Clean up function
    return () => {
      if (isRTMConnected) {
        removeEventListener('message', handleSubtitleMessage);
        disconnectRTM();
      }
    };
  } else {
    // If there's no roomId, just return a clean-up function
    return () => {
      // No RTM connection or listener to clean up
    };
  }
  }, [isRTMConnected, addEventListener, removeEventListener, roomId, connectRTM, disconnectRTM]);

  // Example function to publish a message (could be triggered by some user action)
  const handleSendMessage = async () => {
    if (roomId && isRTMConnected) {
      await rtmPublish(roomId, 'Hello Room!');
    }
  };

  return (
    <AspectCard
      className={`${style.container} ${compact ? style.compact : ""} ${className || ""}`}
      active={participant.active}
      cover={cover}
    >
      <VideoView ref={ref} peerId={participant.peerId} videoTrack={participant.videoTrack} />

      {!participant.videoTrack && (
        <Avatar
          size={80}
          style={{ backgroundColor: "#1890ff", cursor: "pointer" }}
        >
          {participant.peerName}
        </Avatar>
      )}

      <div
        className={`${style['only-name']} ${isMonitorMode ? style['monitor-name'] : ''}`}
      >
        {participant.peerName}
      </div>

      {showTag && !isMonitorMode && (
        <Tag className={style.tag}>
          {!participant.audio && <AudioIcon active={false} />}
          <span className={style.name}>
            {participant.peerName}
            {participant.local ? '(我)' : ''}
          </span>
        </Tag>
      )}

      {showTool && <Tool className={style.tool} participant={participant} />}
          {/* 字幕显示区域 */}
      {caption && (
        <div className={style.caption}>
           {/* {participant.subTitle && (
            <span className={style.captions}>四边伐鼓雪海涌，三军大呼阴山动</span> // 显示实时字幕
          )} */}
          {subtitles.map((subtitle, index) => (
            <div key={index} className={style.captionText}>
              <span className={style.captions}>四边伐鼓雪海涌，三军大呼阴山动</span>
            </div>
          ))}
        </div>
      )}
    </AspectCard>
  );
});

export default VideoCard;
